import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { useDebounce } from '@/hooks/useDebounce';
import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  CollabQuotePreviewResponse,
  CollabSettlementQuote,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { COLLABS_PATH } from './collab-keys';

export const COLLAB_QUOTE_PREVIEW_PATH = `${COLLABS_PATH}/settlement-quote-preview`;

/** Keyed on what is priced, so a corrected figure is repriced and a restored one is not. */
export const collabQuotePreviewQueryKey = (
  brandId: string,
  amountMinorUnits: number,
  currency: string
) => ['collabs', brandId, 'quote-preview', amountMinorUnits, currency] as const;

/**
 * Long enough that typing a four-figure amount costs one request rather than
 * four, short enough that the panel answers while the brand is still looking at
 * the box it just filled.
 */
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * What the route accepts: whole minor units, one to five million. Mirrored here
 * so a half-typed figure never earns a 422 on a panel whose whole job is to
 * reassure — the refusal would be louder than the estimate it replaces.
 */
const MIN_AMOUNT_MINOR_UNITS = 1;
const MAX_AMOUNT_MINOR_UNITS = 5_000_000;

const isPriceable = (amountMinorUnits: number | null): amountMinorUnits is number =>
  typeof amountMinorUnits === 'number' &&
  Number.isInteger(amountMinorUnits) &&
  amountMinorUnits >= MIN_AMOUNT_MINOR_UNITS &&
  amountMinorUnits <= MAX_AMOUNT_MINOR_UNITS;

const isCurrencyCode = (currency: string | null): currency is string =>
  typeof currency === 'string' && /^[A-Za-z]{3}$/.test(currency);

/**
 * What this collab would cost the brand, priced by the server while the brief
 * is still being written.
 *
 * A read, not a command: the route writes nothing and freezes nothing, and the
 * answer changes if the brand's legal country changes — which is exactly why it
 * is asked again rather than remembered. The same policy objects price it and
 * the real charge, so the composer and the payment panel cannot disagree.
 *
 * The amount is debounced because it is typed: quoting every keystroke would
 * hammer a route for figures the brand is still in the middle of writing. The
 * currency is not — it is picked once, and delaying it would leave a stale
 * price on screen after a deliberate switch.
 *
 * Failure is silent on purpose. This is a courtesy beside a brief being
 * written, so a refusal, a timeout or an outage hands back no quote and the
 * panel simply does not appear. No retry: three attempts to compute a courtesy
 * is three times the noise for the same nothing.
 */
export const useCollabQuotePreviewQuery = (
  amountMinorUnits: number | null,
  currency: string | null
): { quote: CollabSettlementQuote | null; isLoading: boolean } => {
  const { data: brand } = useCurrentBrandQuery();
  const brandId = brand?.id ?? '';

  const debouncedAmount = useDebounce(amountMinorUnits, PREVIEW_DEBOUNCE_MS);
  const enabled = Boolean(brandId) && isPriceable(debouncedAmount) && isCurrencyCode(currency);

  const { data, isLoading } = useQuery<CollabSettlementQuote, Error>({
    queryKey: collabQuotePreviewQueryKey(brandId, debouncedAmount ?? 0, currency ?? ''),
    queryFn: async () => {
      const { data: envelope } = await api.post<ApiResponse<CollabQuotePreviewResponse>>(
        COLLAB_QUOTE_PREVIEW_PATH,
        { amountMinorUnits: debouncedAmount, currency }
      );
      return (envelope.data as CollabQuotePreviewResponse).quote;
    },
    enabled,
    retry: false,
    // The price depends on the brand's plan and legal country, neither of which
    // moves while a brief is being written. Re-opening the composer on the same
    // figure reads the cache instead of the route.
    staleTime: 5 * 60 * 1000,
  });

  return { quote: data ?? null, isLoading: enabled && isLoading };
};
